import React, { useState, useRef, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { DatePicker, DatesProvider } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import "dayjs/locale/ar";

/** Arabic weekday labels Sun→Sat (dayjs day()). Readable short forms. */
const AR_WEEKDAY_LABELS = [
  "أحد",
  "إثنين",
  "ثلاثاء",
  "أربعاء",
  "خميس",
  "جمعة",
  "سبت",
];

const arabicWeekdayFormat = (date) => AR_WEEKDAY_LABELS[dayjs(date).day()];
const englishWeekdayFormat = (date) =>
  dayjs(date).locale("en").format("ddd"); // Mon, Tue, …

function toYmd(value) {
  if (!value) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  }
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
}

function rangesEqual(a, b) {
  if (!a || !b) return false;
  return toYmd(a[0]) === toYmd(b[0]) && toYmd(a[1]) === toYmd(b[1]);
}

/** Single-date input with Arabic calendar locale (replaces native type="date"). */
export const LocalizedDateInput = ({
  value = "",
  onChange = () => {},
  className = "",
  disabled = false,
  placeholder,
  minDate = null,
  maxDate = null,
}) => {
  const { i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar" || i18n.dir() === "rtl";
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const resolvedPlaceholder =
    placeholder || (isArabic ? "يوم/شهر/سنة" : "dd/mm/yyyy");
  const displayText = value
    ? dayjs(value).locale(isArabic ? "ar" : "en").format("DD/MM/YYYY")
    : "";
  const pickerValue = value || null;
  const todayYmd = dayjs().format("YYYY-MM-DD");
  const isTodayAllowed =
    (!minDate || todayYmd >= minDate) && (!maxDate || todayYmd <= maxDate);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const emitChange = (ymd) => {
    if (ymd) {
      if (minDate && ymd < minDate) return;
      if (maxDate && ymd > maxDate) return;
    }
    onChange({ target: { value: ymd || "" } });
  };

  const handleDateChange = (date) => {
    const ymd = toYmd(date);
    emitChange(ymd);
    if (ymd) setShowDropdown(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    emitChange("");
    setShowDropdown(false);
  };

  const handleToday = (e) => {
    e.stopPropagation();
    if (!isTodayAllowed) return;
    emitChange(todayYmd);
    setShowDropdown(false);
  };

  return (
    <div
      className={`oh-localized-date position-relative ${className}`.trim()}
      ref={dropdownRef}
    >
      <button
        type="button"
        className={`oh-field__control custom-date oh-localized-date__input${
          disabled ? " is-disabled" : ""
        }${!displayText ? " is-placeholder" : ""}`}
        onClick={() => !disabled && setShowDropdown((prev) => !prev)}
        disabled={disabled}
        aria-expanded={showDropdown}
        aria-haspopup="dialog"
      >
        <span className="oh-localized-date__value text-truncate">
          {displayText || resolvedPlaceholder}
        </span>
        <i
          className="bi bi-calendar-event oh-localized-date__icon"
          aria-hidden="true"
        />
      </button>

      {showDropdown && (
        <div
          className={`custom-datepicker-dropdown oh-localized-date__dropdown ${
            isArabic ? "rtl" : "ltr"
          }`}
        >
          <DatesProvider
            settings={{
              locale: isArabic ? "ar" : "en",
              firstDayOfWeek: isArabic ? 6 : 0,
            }}
          >
            <DatePicker
              locale={isArabic ? "ar" : "en"}
              weekdayFormat={isArabic ? arabicWeekdayFormat : "dd"}
              value={pickerValue}
              onChange={handleDateChange}
              size="xs"
              minDate={minDate || undefined}
              maxDate={maxDate || undefined}
            />
          </DatesProvider>
          <div className="oh-localized-date__footer">
            <button
              type="button"
              className="oh-localized-date__link"
              onClick={handleClear}
            >
              {isArabic ? "مسح" : "Clear"}
            </button>
            <button
              type="button"
              className="oh-localized-date__link"
              onClick={handleToday}
              disabled={!isTodayAllowed}
            >
              {isArabic ? "اليوم" : "Today"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomDateRangePicker = ({
  value,
  onChange,
  placeholder = "Select date range",
  className = "",
  disabled = false,
}) => {
  const { i18n } = useTranslation("translation");
  const isRTL = i18n.language === "ar" || i18n.dir() === "rtl";
  const isArabic = String(i18n.language || "").toLowerCase().startsWith("ar");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const today = dayjs().startOf("day");

  const presets = useMemo(
    () => [
      {
        label: isArabic ? "اليوم" : "Today",
        value: [today.format("YYYY-MM-DD"), today.format("YYYY-MM-DD")],
      },
      {
        label: isArabic ? "آخر 30 يومًا" : "Last 30 days",
        value: [
          today.subtract(29, "day").format("YYYY-MM-DD"),
          today.format("YYYY-MM-DD"),
        ],
      },
      {
        label: isArabic ? "آخر 7 أيام" : "Last 7 days",
        value: [
          today.subtract(6, "day").format("YYYY-MM-DD"),
          today.format("YYYY-MM-DD"),
        ],
      },
      {
        label: isArabic ? "آخر 14 يومًا" : "Last 14 days",
        value: [
          today.subtract(13, "day").format("YYYY-MM-DD"),
          today.format("YYYY-MM-DD"),
        ],
      },
      {
        label: isArabic ? "هذا الشهر" : "This Month",
        value: [
          today.startOf("month").format("YYYY-MM-DD"),
          today.endOf("month").format("YYYY-MM-DD"),
        ],
      },
      {
        label: isArabic ? "الشهر الماضي" : "Last Month",
        value: [
          today.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
          today.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
        ],
      },
    ],
    // Recompute once per calendar day / language
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isArabic, today.format("YYYY-MM-DD")]
  );

  const normalizedValue = useMemo(() => {
    if (!value) return [null, null];
    return [toYmd(value[0]), toYmd(value[1])];
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDateChange = (val) => {
    const next = Array.isArray(val)
      ? [toYmd(val[0]), toYmd(val[1])]
      : [null, null];
    onChange(next);
    if (next[0] && next[1]) {
      setShowDropdown(false);
    }
  };

  const matchedPreset = presets.find((p) =>
    rangesEqual(normalizedValue, p.value)
  );

  const getDisplayText = () => {
    if (matchedPreset) return matchedPreset.label;
    if (!normalizedValue?.[0]) return "";
    const dateFormat = "DD/MM/YYYY";
    const start = dayjs(normalizedValue[0]).format(dateFormat);
    if (!normalizedValue[1]) return start;
    const end = dayjs(normalizedValue[1]).format(dateFormat);
    return start === end ? start : `${start} - ${end}`;
  };

  const displayText = getDisplayText();

  const handleClearClick = (e) => {
    e.stopPropagation();
    onChange([null, null]);
    setShowDropdown(false);
  };

  return (
    <div
      className={`oh-date-range-field position-relative ${className}`}
      ref={dropdownRef}
    >
      <div className="oh-select__input-wrap custom-input-relative-wrapper">
        <div
          className={`oh-field__control oh-date-range-trigger${
            disabled ? " is-disabled" : ""
          }${!displayText ? " is-placeholder" : ""}`}
          onClick={() => !disabled && setShowDropdown(!showDropdown)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (!disabled) setShowDropdown(!showDropdown);
            }
          }}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={showDropdown}
        >
          <span className="text-truncate">{displayText || placeholder}</span>
        </div>

        {!displayText && !showDropdown && (
          <span className="oh-select__icon custom-input-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        )}

        {(showDropdown || displayText) && (
          <span
            className="oh-select__clear custom-input-clear"
            onClick={handleClearClick}
            role="button"
            tabIndex={0}
            aria-label="Clear"
          >
            ✕
          </span>
        )}
      </div>

      {showDropdown && (
        <div
          className={`custom-datepicker-dropdown custom-datepicker-dropdown--compact ${
            isRTL ? "rtl" : "ltr"
          }`}
        >
          <div className="oh-dp-root">
            <div className="oh-dp-presets" role="listbox" aria-label={placeholder}>
              {presets.map((preset) => {
                const isActive = rangesEqual(normalizedValue, preset.value);
                return (
                  <button
                    key={preset.label}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={`oh-dp-preset${isActive ? " is-active" : ""}`}
                    onClick={() => {
                      onChange(preset.value);
                      setShowDropdown(false);
                    }}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
            <DatesProvider
              settings={{
                locale: isArabic ? "ar" : "en",
                firstDayOfWeek: isArabic ? 6 : 1,
              }}
            >
              <DatePicker
                type="range"
                size="xs"
                value={normalizedValue}
                onChange={handleDateChange}
                locale={isArabic ? "ar" : "en"}
                weekdayFormat={isArabic ? arabicWeekdayFormat : englishWeekdayFormat}
                allowSingleDateInRange
                classNames={{
                  day: "oh-dp-day",
                  calendarHeader: "oh-dp-header",
                  calendarHeaderControl: "oh-dp-header-control",
                  calendarHeaderLevel: "oh-dp-header-level",
                  weekday: "oh-dp-weekday",
                  weekdaysRow: "oh-dp-weekdays",
                  monthCell: "oh-dp-cell",
                  monthRow: "oh-dp-row",
                  month: "oh-dp-month",
                  monthThead: "oh-dp-thead",
                  monthTbody: "oh-dp-tbody",
                }}
              />
            </DatesProvider>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDateRangePicker;
